import { useCallback } from 'react';
import { CreateCampaignState } from './useCreateCampaignState';
import { useParams } from 'react-router';
import useNotifications from 'core/notifications/useNotifications';
import { useTranslation } from 'react-i18next';
import { useCreateOffsiteCampaignMutation } from 'server/campaigns/mutations/createOffsiteCampaignMutation/useCreateOffsiteCampaignMutation';
import { useNavigate } from 'core/router/useNavigate';
import { BulkRetargetingRoutes } from 'sections/bulk-retargeting-campaigns/routes';
import { useFeatureFlags } from 'core/app/AppContext';
import { useCreateBulkRetargetingCampaignMutation } from 'sections/bulk-retargeting-campaigns/queries/useCreateBulkRetargetingCampaign';

export const useCreateCampaign = (state: CreateCampaignState) => {
  const { t } = useTranslation('campaigns');
  const { siteId } = useParams<{ siteId: string }>();
  const { showNotification } = useNotifications();
  const navigate = useNavigate();
  const { flagEnabled } = useFeatureFlags();

  const isBulkyV2 = flagEnabled('ld-bulk-targeting');

  const offsiteMutation = useCreateOffsiteCampaignMutation();
  const bulkyMutation = useCreateBulkRetargetingCampaignMutation();

  const createCampaign = useCallback(async () => {
    const mutateFn = isBulkyV2 ? bulkyMutation.mutateAsync : offsiteMutation.mutateAsync;

    try {
      const response = await mutateFn({
        version: 'v2',
        siteId: siteId,
        type: 'bulkRetargeting',
        state: 'DRAFT',
        name: state.name,
        segments: [],
      });

      navigate(BulkRetargetingRoutes.EDIT_AUDIENCE, { siteId, campaignId: response.data.id });
    } catch (error) {
      showNotification(t('bulkRetargetingCampaigns.createModal.failedCreateMessage'), 'error');
    }
  }, [
    bulkyMutation.mutateAsync,
    isBulkyV2,
    navigate,
    offsiteMutation.mutateAsync,
    showNotification,
    siteId,
    state.name,
    t,
  ]);

  if (isBulkyV2) {
    return { createCampaign, ...bulkyMutation };
  }

  return { createCampaign, ...offsiteMutation };
};
